import useAuth from '@app/auth/use-auth';
import { useCCF } from '@app/ccf/company-report/ccf-context';
import { reportSlugs } from '@app/ccf/company-report/slugs';
import { PACKAGING_ITEMS_RATING } from '@app/ccf/company-report/utils';
import PreviewSheet from '@app/components/preview/preview-sheet';
import { PreviewSheetBarChart } from '@app/components/preview/preview-sheet-bar-chart';
import PreviewSheetSection from '@app/components/preview/preview-sheet-chart-section';
import { useAccount } from '@shared/api';
import { useGetPackagingPreview } from '@shared/api/hooks/ccf/activities/packaging';
import { itemsByIntensityChartConfig } from '@shared/components/chart/configs';
import SimplePieChart from '@shared/components/chart/simple-pie-chart';
import Text from '@shared/components/content/text';
import Unit from '@shared/components/content/unit';
import { useGetFilterLabels } from '@shared/components/data-table/hooks/use-get-filter-labels';
import { ChartConfig } from '@shared/components/ui/chart';
import { formatCompact, getCurrencySymbol } from '@shared/lib/format';
import { capitalizeFirstLetter, formatNumber } from '@shared/lib/utils';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

export default function PackagingPreviewSheet() {
  const [searchParams] = useSearchParams();
  const [isOpen, setIsOpen] = useState(false);

  const time_periods = searchParams.get('period')?.split('.');
  const suppliers = searchParams.get('supplier')?.split('.');

  const { flatLabels: tags } = useGetFilterLabels([
    'supplier',
    'period',
    'site',
  ]);

  const { uuid: reportUuid, categories } = useCCF();

  const category = categories?.find(
    (item) => item.slug === reportSlugs.packaging
  );

  const { data: packagingPreviewData, status } = useGetPackagingPreview({
    companyReportUuid: reportUuid!,
    params: {
      suppliers,
      time_periods,
    },
    enabled: true,
  });

  const { user } = useAuth();
  const hasCostFeature = !!user?.features.cost;
  const { data: account } = useAccount(true);

  const totalEmissionsTonnes = packagingPreviewData?.totalEmissionsTonnes;
  const totalWeightKg = packagingPreviewData?.totalWeightKg;
  const totalSpend =
    packagingPreviewData?.totalSpend != null
      ? Number(packagingPreviewData.totalSpend)
      : null;

  const emissionsByMaterial = packagingPreviewData?.emissionsByMaterial?.map(
    (category) => {
      return {
        material: capitalizeFirstLetter(category.material),
        emissions: category.totalEmissionsTonnes,
        fill: 'var(--chart-3)',
      };
    }
  );

  const itemsByRating = packagingPreviewData?.itemsByCarbonIntensity?.map(
    (category) => {
      return {
        category: category.carbonIntensityCategory,
        items: category.numberOfItems,
        fill: PACKAGING_ITEMS_RATING[
          category.carbonIntensityCategory as keyof typeof PACKAGING_ITEMS_RATING
        ].color,
      };
    }
  );

  const numberOfItems = packagingPreviewData?.itemsByCarbonIntensity?.reduce(
    (acc, category) => {
      return acc + category.numberOfItems;
    },
    0
  );
  const chartConfig = {
    emissions: {
      label: 'Emissions',
      color: 'hsl(var(--chart-1))',
      unit: 'tCO2e',
    },
  } satisfies ChartConfig;

  return (
    <PreviewSheet
      title={`Scope ${category?.scope} - ${category?.name}`}
      status={status}
      metrics={[
        {
          title: 'Total emissions',
          value: formatNumber(totalEmissionsTonnes!),
          unit: 'tCO2e',
        },
        {
          title: 'Total packaging weight',
          value: formatNumber(totalWeightKg! / 1000),
          unit: 't',
        },
        ...(hasCostFeature && totalSpend != null
          ? [
              {
                title: 'Total spend',
                value: (
                  <>
                    {/* Currency symbol matches the gray-400 unit colour so it
                        reads as a unit rather than blending into the figure. */}
                    <span className="mr-1 text-gray-400">
                      {getCurrencySymbol(account?.defaultCurrency)}
                    </span>
                    {formatCompact(totalSpend)}
                  </>
                ),
                unit: '',
              },
            ]
          : []),
      ]}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      tags={tags}
    >
      {packagingPreviewData ? (
        <>
          <PreviewSheetSection title="Items by carbon intensity">
            <div className="flex items-center space-y-2">
              <SimplePieChart
                dataKey="items"
                nameKey="category"
                chartConfig={itemsByIntensityChartConfig}
                chartData={itemsByRating!.reverse()}
                innerRadius={50}
                className="h-40 w-48"
                margin={{ top: 0, right: 0, bottom: 0, left: -0 }}
                centerLabel={{
                  value: numberOfItems!,
                  label: 'items',
                }}
              />
              <div className="flex flex-col items-start justify-start gap-y-2">
                {(
                  ['Low', 'Medium', 'High'] as Array<
                    keyof typeof PACKAGING_ITEMS_RATING
                  >
                ).map((rating, idx) => {
                  return (
                    <div key={idx} className="flex items-start gap-2">
                      <div
                        className="mt-0.5 size-4 rounded-sm"
                        style={{
                          backgroundColor: PACKAGING_ITEMS_RATING[rating].color,
                        }}
                      ></div>
                      <div>
                        <Text className="mt-0">{rating}</Text>{' '}
                        <span className="flex items-baseline space-x-1">
                          <Text variant="unit">
                            {PACKAGING_ITEMS_RATING[rating].threshold}
                          </Text>
                          <Unit variant="kgCO2e/kg" />
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </PreviewSheetSection>

          <PreviewSheetSection
            title="Emissions by material"
            unit="tCO2e"
            className="h-full"
          >
            <PreviewSheetBarChart
              nameKey="material"
              chartData={emissionsByMaterial}
              chartConfig={chartConfig}
            />
          </PreviewSheetSection>
        </>
      ) : (
        <Text className="text-center">
          No data available for the selected filters.
        </Text>
      )}
    </PreviewSheet>
  );
}
